import { useCallback, useEffect, useState } from 'react'
import CheckIcon from '../components/CheckIcon'
import KebabIcon from '../components/KebabIcon'
import NewSpaceDialog from '../components/NewSpaceDialog'
import PlusIcon from '../components/PlusIcon'
import Segmented from '../components/Segmented'
import { api } from '../lib/api'
import {
  daysUntil,
  formatRelativeActivity,
  formatRelativeDays,
  formatShortDate,
} from '../lib/format'

// The admin's landing view, laid out after the admin screens proposal: a dark
// band summarising what needs attention, an ice hero with the title and the
// New space button, then the list — one row per space, i.e. per engagement.
// Show: Active / Archived / All (the same manual, reversible `archived_at`
// stamp the rest of the app uses), a search over client, engagement and
// principal, and every column sorts on a click of its header.

// The Ends column turns urgent inside a week, and the band counts the same
// window. A date already passed is shown plainly — that space is either
// archived or about to be.
const SOON_DAYS = 7

const COLUMNS = [
  { key: 'engagement', label: 'Engagement' },
  { key: 'principal', label: 'Principal' },
  { key: 'ends', label: 'Ends' },
  { key: 'progress', label: 'Progress' },
  { key: 'new', label: 'New' },
  { key: 'fancy', label: 'Fancy notes' },
  { key: 'activity', label: 'Last activity' },
]

const SORTS = {
  engagement: (space) => `${space.client_name} ${space.engagement}`.toLowerCase(),
  principal: (space) => space.principal_name.toLowerCase(),
  ends: (space) => space.ends_at,
  progress: (space) => (space.staff_count ? space.completed_count / space.staff_count : -1),
  new: (space) => space.new_count,
  fancy: (space) => space.sent_to_fancy_notes_at || '',
  activity: (space) => space.last_activity_at || space.created_at,
}

// Text columns start ascending; counts and dates start with the biggest or
// newest first, which is what someone clicking "New" or "Last activity" wants.
const DESC_FIRST = new Set(['progress', 'new', 'activity', 'fancy'])

// What the Principal cell says under the name: the state of their personal link.
function accessLine(space) {
  const access = space.access ?? { status: 'not_shared' }
  switch (access.status) {
    case 'active':
      return access.last_used_at
        ? {
            on: true,
            text: `Link active · opened ${formatShortDate(access.last_used_at, { year: false })}`,
          }
        : { on: false, text: 'Link sent · not opened yet' }
    case 'revoked':
      return { on: false, text: 'Access revoked' }
    case 'expired':
      return { on: false, text: space.archived_at ? 'Access closed' : 'Link expired' }
    default:
      return { on: false, text: 'Not shared yet' }
  }
}

export default function SpacesPage({ onOpenSpace }) {
  const [spaces, setSpaces] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [flash, setFlash] = useState(null)
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState({ key: 'ends', dir: 'asc' })
  const [busyId, setBusyId] = useState(null)
  const [menu, setMenu] = useState(null) // { space, top, right }
  const [templates, setTemplates] = useState(null) // loaded when the dialog opens
  const [openingNew, setOpeningNew] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api('/api/v1/spaces?include_archived=true')
      .then(setSpaces)
      .catch((err) => setError(err.detail || 'Could not load spaces.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  // The kebab menu is positioned from its button, so anything that moves the
  // button — a scroll anywhere, a resize — closes it rather than leaving it
  // floating over the wrong row.
  useEffect(() => {
    if (!menu) return undefined
    const close = () => setMenu(null)
    function handleKey(event) {
      if (event.key === 'Escape') close()
    }
    function handleMouse(event) {
      if (!event.target.closest?.('.menu, .kebab')) close()
    }
    document.addEventListener('mousedown', handleMouse)
    document.addEventListener('keydown', handleKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', handleMouse)
      document.removeEventListener('keydown', handleKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [menu])

  async function openNew() {
    setFlash(null)
    setOpeningNew(true)
    try {
      setTemplates(await api('/api/v1/templates'))
    } catch (err) {
      setFlash({ type: 'error', text: err.detail || 'Could not load the templates.' })
      setOpeningNew(false)
    }
  }

  function closeNew() {
    setTemplates(null)
    setOpeningNew(false)
  }

  function toggleMenu(event, space) {
    event.stopPropagation()
    if (menu?.space.id === space.id) {
      setMenu(null)
      return
    }
    const rect = event.currentTarget.getBoundingClientRect()
    setMenu({ space, top: rect.bottom + 4, right: window.innerWidth - rect.right })
  }

  function toggleSort(key) {
    setSort((current) =>
      current.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: DESC_FIRST.has(key) ? 'desc' : 'asc' },
    )
  }

  async function handleArchive(space, archived) {
    setMenu(null)
    setBusyId(space.id)
    setFlash(null)
    try {
      await api(`/api/v1/spaces/${space.id}/archived`, { method: 'PUT', body: { archived } })
      const name = `${space.client_name} · ${space.engagement}`
      setFlash({
        type: 'success',
        text: archived
          ? `${name} archived — find it under Archived.`
          : `${name} moved back to the active list.`,
      })
      load()
    } catch (err) {
      setFlash({
        type: 'error',
        text: err.detail || `Could not ${archived ? 'archive' : 'unarchive'} this space.`,
      })
    } finally {
      setBusyId(null)
    }
  }

  const all = spaces || []
  const active = all.filter((space) => !space.archived_at)
  const archived = all.filter((space) => space.archived_at)
  const shown = filter === 'active' ? active : filter === 'archived' ? archived : all
  const needle = query.trim().toLowerCase()
  const matched = needle
    ? shown.filter((space) =>
        [space.client_name, space.engagement, space.principal_name]
          .join(' ')
          .toLowerCase()
          .includes(needle),
      )
    : shown
  const visible = matched.slice().sort((a, b) => {
    const left = SORTS[sort.key](a)
    const right = SORTS[sort.key](b)
    const order = left < right ? -1 : left > right ? 1 : 0
    return sort.dir === 'asc' ? order : -order
  })

  const newSpaceButton = (
    <button type="button" className="btn btn--primary" onClick={openNew} disabled={openingNew}>
      <PlusIcon size={16} />
      New space
    </button>
  )

  return (
    <main>
      {!loading && !error && spaces && <Attention active={active} />}

      <section className="disc-band disc-band--alt">
        <div className="hero">
          <div className="hero__text">
            <p className="overline hero__eyebrow">Administration</p>
            <h1 className="text-h1">Spaces</h1>
            <p className="text-body-lg hero__lede">
              One space per engagement. Its links, and the principal’s access, end when the space
              does.
            </p>
          </div>
          {newSpaceButton}
        </div>
      </section>

      <div className="admin-main">
        {flash && (
          <div
            className={`alert alert--${flash.type} text-body-sm`}
            role={flash.type === 'error' ? 'alert' : 'status'}
          >
            {flash.text}
          </div>
        )}

        {loading && (
          <div className="card" role="status">
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
              Loading spaces…
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert--error" role="alert">
            <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
              <p className="text-body-md">{error}</p>
              <div>
                <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && spaces && spaces.length === 0 && (
          <div className="spaces-table">
            <div className="spaces-empty">
              <h3 className="text-h5">No spaces yet</h3>
              <p className="text-body-sm" style={{ color: 'var(--ink-500)' }}>
                Create one for each engagement you run.
              </p>
              <div style={{ marginTop: 'var(--space-xs)' }}>{newSpaceButton}</div>
            </div>
          </div>
        )}

        {!loading && !error && spaces && spaces.length > 0 && (
          <>
            <div className="list-tools">
              <Segmented
                label="Show"
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: 'All', count: all.length },
                  { value: 'active', label: 'Active', count: active.length },
                  { value: 'archived', label: 'Archived', count: archived.length },
                ]}
              />
              <label className="search">
                <span className="sr-only">Search spaces</span>
                <input
                  type="search"
                  className="field__input search__input"
                  placeholder="Search client, engagement or principal"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            </div>

            <div className="spaces-table" role="table" aria-label="Spaces">
              <div className="spaces-row spaces-row--head" role="row">
                {COLUMNS.map(({ key, label }) => (
                  <span
                    key={key}
                    role="columnheader"
                    aria-sort={
                      sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                  >
                    <button type="button" className="spaces-sort" onClick={() => toggleSort(key)}>
                      {label}
                      {sort.key === key && (
                        <span aria-hidden="true">{sort.dir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </button>
                  </span>
                ))}
                <span role="columnheader" className="sr-only">
                  Actions
                </span>
              </div>

              {visible.length === 0 && (
                <p className="spaces-none text-body-sm" role="status">
                  {needle
                    ? 'No spaces match that search.'
                    : filter === 'active'
                      ? 'No active spaces.'
                      : 'No archived spaces.'}
                </p>
              )}

              {visible.map((space) => (
                <SpaceRow
                  key={space.id}
                  space={space}
                  busy={busyId === space.id}
                  menuOpen={menu?.space.id === space.id}
                  onOpen={() => onOpenSpace(space.id, 'people')}
                  onMenu={(event) => toggleMenu(event, space)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {menu && (
        <div
          className="menu"
          role="menu"
          aria-label={`Actions for ${menu.space.client_name}`}
          style={{ top: menu.top, right: menu.right }}
        >
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenu(null)
              onOpenSpace(menu.space.id, 'people')
            }}
          >
            Open
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setMenu(null)
              onOpenSpace(menu.space.id, 'settings')
            }}
          >
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => handleArchive(menu.space, !menu.space.archived_at)}
          >
            {menu.space.archived_at ? 'Unarchive' : 'Archive'}
          </button>
        </div>
      )}

      {templates && (
        <NewSpaceDialog
          templates={templates}
          onClose={closeNew}
          onCreated={(space) => {
            closeNew()
            onOpenSpace(space.id, 'people')
          }}
        />
      )}
    </main>
  )
}

// The dark band: one sentence about the live spaces and three figures that
// say what is waiting — results nobody has read, principals who have not
// opened their link, spaces about to end. Archived spaces count for nothing.
function Attention({ active }) {
  const newResults = active.reduce((sum, space) => sum + (space.new_count || 0), 0)
  const unopened = active.filter(
    (space) => space.access?.status === 'active' && !space.access.last_used_at,
  ).length
  const endingSoon = active.filter((space) => {
    const days = daysUntil(space.ends_at)
    return days >= 0 && days <= SOON_DAYS
  }).length
  const anyCompleted = active.some((space) => space.completed_count > 0)

  let headline
  if (active.length === 0) {
    headline = 'No spaces are live. Create one for the next engagement.'
  } else {
    const live = active.length === 1 ? 'One space is live.' : `${active.length} spaces are live.`
    const waiting =
      newResults === 0
        ? anyCompleted
          ? 'Nothing new to read.'
          : 'Nothing has come back yet.'
        : newResults === 1
          ? 'One result is waiting to be read.'
          : `${newResults} results are waiting to be read.`
    headline = `${live} ${waiting}`
  }

  const figures = [
    { value: newResults, label: newResults === 1 ? 'new result to read' : 'new results to read' },
    {
      value: unopened,
      label:
        unopened === 1
          ? 'principal hasn’t opened their link'
          : 'principals haven’t opened their link',
    },
    {
      value: endingSoon,
      label: endingSoon === 1 ? 'space ending this week' : 'spaces ending this week',
    },
  ]

  return (
    <section className="disc-band disc-band--dark" aria-labelledby="attention-title">
      <div className="disc-band__inner attention">
        <div className="disc-lead">
          <p className="overline">Needs your attention</p>
          <h2 id="attention-title" className="disc-lead__title">
            {headline}
          </h2>
        </div>
        <ol className="attention__stats">
          {figures.map((figure) => (
            <li key={figure.label} className="attention__stat">
              <p className="attention__num">{figure.value}</p>
              <p className="attention__label">{figure.label}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

function SpaceRow({ space, busy, menuOpen, onOpen, onMenu }) {
  const days = daysUntil(space.ends_at)
  const soon = days >= 0 && days <= SOON_DAYS
  const access = accessLine(space)
  const total = space.staff_count ?? 0
  const done = space.completed_count ?? 0
  return (
    <div
      className={`spaces-row spaces-row--body${space.archived_at ? ' spaces-row--archived' : ''}`}
      role="row"
      onClick={onOpen}
    >
      <div className="spaces-cell" role="cell">
        {/* The name is the row's keyboard target; the click on the rest of the
            row is the mouse's. Stop the button's click so one open is one open. */}
        <button
          type="button"
          className="spaces-cell__link"
          onClick={(event) => {
            event.stopPropagation()
            onOpen()
          }}
        >
          <span className="spaces-client">{space.client_name}</span>
          <span className="spaces-engagement">
            {space.engagement}
            {space.archived_at ? ' · archived' : ''}
          </span>
        </button>
      </div>
      <div className="spaces-cell" role="cell">
        <span className="spaces-principal">{space.principal_name}</span>
        <span className={`access-line${access.on ? ' access-line--on' : ''}`}>
          <span className={`access-dot${access.on ? ' access-dot--on' : ''}`} aria-hidden="true" />
          {access.text}
        </span>
      </div>
      <div className="spaces-cell" role="cell">
        <span>{formatShortDate(space.ends_at)}</span>
        <span className={`spaces-sub${soon ? ' spaces-sub--soon' : ''}`}>
          {formatRelativeDays(space.ends_at)}
        </span>
      </div>
      <div className="spaces-cell spaces-progress" role="cell">
        <span className="spaces-progress__text">
          <strong>{done}</strong>
          <span className="spaces-progress__of"> of {total} completed</span>
        </span>
        <span
          className="progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          aria-label="Conversations completed"
        >
          <span
            className="progress__fill"
            style={{ width: total ? `${Math.round((done / total) * 100)}%` : 0 }}
          />
        </span>
      </div>
      <div className="spaces-cell" role="cell">
        {space.new_count > 0 ? (
          <span className="chip chip--new">{space.new_count} new</span>
        ) : (
          <span className="spaces-zero" aria-label="Nothing new">
            —
          </span>
        )}
      </div>
      <div className="spaces-cell" role="cell">
        {space.sent_to_fancy_notes_at ? (
          <span className="spaces-sent">
            <CheckIcon size={14} />
            Sent {formatShortDate(space.sent_to_fancy_notes_at, { year: false })}
          </span>
        ) : (
          <span className="spaces-zero" aria-label="Not sent to Fancy Notes">
            —
          </span>
        )}
      </div>
      <div className="spaces-cell" role="cell">
        <span>
          {space.last_activity_at
            ? formatRelativeActivity(space.last_activity_at)
            : `Created ${formatShortDate(space.created_at, { year: false })}`}
        </span>
      </div>
      <div className="spaces-cell" role="cell">
        <button
          type="button"
          className="kebab"
          aria-label={`Actions for ${space.client_name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          disabled={busy}
          onClick={onMenu}
        >
          <KebabIcon size={16} />
        </button>
      </div>
    </div>
  )
}
